import { useState, type Dispatch, type SetStateAction } from 'react';

/** Направление сортировки таблицы списка. */
export type SortDirection = 'asc' | 'desc';

/** Состояние сортировки: поле колонки и направление. */
export interface TableSortState<TField extends string = string> {
  field: TField;
  direction: SortDirection;
}

/** Индекс первой страницы списка после смены сортировки. */
const FIRST_PAGE = 0;

/** Направление для только что выбранного поля (контракт: новое поле → asc). */
const NEW_FIELD_DIRECTION: SortDirection = 'asc';

/**
 * Сортировка таблиц списков (задачи, сделки, компании, контакты).
 *
 * Контракт: новое поле → asc; активное поле → toggle asc/desc; каждый клик сбрасывает page на 0.
 *
 * @param defaultSort начальное поле и направление (обычно createdAt,desc)
 * @param setPage сеттер пагинации списка — вызывается с 0 при смене сортировки
 */
export function useTableSort<TField extends string>(
  defaultSort: TableSortState<TField>,
  setPage: (page: number) => void,
): {
  sort: TableSortState<TField>;
  setSort: Dispatch<SetStateAction<TableSortState<TField>>>;
  handleSortClick: (field: TField) => void;
} {
  const [sort, setSort] = useState<TableSortState<TField>>(defaultSort);

  const handleSortClick = (field: TField): void => {
    setPage(FIRST_PAGE);
    setSort((prev) => {
      const isActiveField = prev.field === field;
      if (!isActiveField) {
        return { field, direction: NEW_FIELD_DIRECTION };
      }
      const direction: SortDirection = prev.direction === 'asc' ? 'desc' : 'asc';
      return { field, direction };
    });
  };

  return { sort, setSort, handleSortClick };
}
